import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import { Fetcher } from "../../components/Fetcher";
import { PowFeature } from "../../components/PowFeature";
import { Button, ListItem, List, Grid, ListItemText } from "@material-ui/core";
import { Link } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { POW_FEATURES } from "../../services/powFeatures.service";
import { getReportsList } from "../../services/api/reports.api";
import { Report } from "../../models/Reports";
import { toReportDetails } from "../../router/navigation";
import { DateDisplay } from "../../components/DateDisplay";

interface ReportsListPageProps {
	productId: string;
}

const useStyles = makeStyles((theme) => ({
	title: {
		marginBottom: theme.spacing(4),
	},
	titleWrapper: {
		maxWidth: theme.breakpoints.values.md,
		display: "flex",
		justifyContent: "space-between",
	},
	list: {
		maxWidth: theme.breakpoints.values.md,
	},
	listItem: {
		boxShadow: theme.shadows[1],
		marginBottom: theme.spacing(2),
		paddingBottom: 12,
	},
	listSecondary: {
		marginTop: theme.spacing(0.5),
		fontSize: theme.typography.caption.fontSize,
	},
	details: {
		fontSize: theme.typography.body2.fontSize,
		marginTop: theme.spacing(1),
		display: "block",
	},
}));

const RenderList = (reports: Report[], productId: string, classes) => {
	return (
		<List className={classes.list}>
			{reports.map((r) => (
				<ListItem key={r.name} className={classes.listItem} data-testid="report-list-item">
					<Grid container>
						<Grid item xs={12}>
							<ListItemText
								classes={{ secondary: classes.listSecondary }}
								primary={
									<div>
										<Grid container justify="space-between">
											<Grid item>
												<Link to={toReportDetails(productId, r.reportId)}>{r.name}</Link>
											</Grid>
											<Grid item>
												<Typography variant="caption">
													updated on <DateDisplay date={r.lastUpdatedDate} />
												</Typography>
											</Grid>
										</Grid>
										<Typography variant="subtitle1" className={classes.details}>
											{r.description}
										</Typography>
									</div>
								}
							/>
						</Grid>
					</Grid>
				</ListItem>
			))}
		</List>
	);
};

/**
 * Fetch and display reports
 * @param props
 */
export const ReportsListPage = (props: ReportsListPageProps) => {
	const classes = useStyles();
	const history = useHistory();

	return (
		<div data-testid={"reports-list-page"}>
			<div className={classes.titleWrapper}>
				<Typography className={classes.title} variant="h4">
					Reports
				</Typography>
				<div>
					<PowFeature feature={POW_FEATURES.paReports_Admin}>
						<div data-testid="report-add-button">
							<Button
								size="small"
								variant="outlined"
								color="primary"
								onClick={() => history.push(`/Products/${props.productId}/Reports/new`)}
							>
								Add new report
							</Button>
						</div>
					</PowFeature>
				</div>
			</div>

			{useMemo(() => {
				return (
					<Fetcher action={getReportsList(props.productId)}>
						{({ data }) => RenderList(data, props.productId, classes)}
					</Fetcher>
				);
			}, [classes, props.productId])}
		</div>
	);
};
